import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import clsx from "clsx";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { v4 as uuidv4 } from "uuid";

import { useAlert } from "react-alert";

import { createFunctionDefinition } from "../actions/functions";
import { RefreshIcon } from "../components/icons/themes/windows10";
import { FunctionTextInput } from "./FunctionTextInput";
import { GitHubSetupModal, isGitHubSetupError } from "./GitHubSetupModal";
import { useFunctionStyles } from "./styles";
import { FunctionType } from "./types";
import { useFunctionDefinitionValidator } from "./useFunctionDefinitionValidator";

export const NewFunctionBuilder = () => {
  const classes = useFunctionStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const alert = useAlert();
  const validateFunctionDefinition = useFunctionDefinitionValidator();
  const [functionName, setFunctionName] = useState("");
  const [functionType, setFunctionType] = useState<FunctionType>(null);
  const [gitHubRepo, setGitHubRepo] = useState("");
  const [description, setDescription] = useState("");
  const [gitHubSetupError, setGitHubSetupError] = useState<string | null>(null);

  const [isSaving, setIsSaving] = useState(false);

  const onSave = () => {
    const newDefinition = {
      id: uuidv4(),
      name: functionName,
      type: functionType,
      source: gitHubRepo,
      description: description,
    };

    const validationError = validateFunctionDefinition(newDefinition);

    if (validationError) {
      alert.error(validationError.message);
      return;
    }

    setIsSaving(true);

    (dispatch(createFunctionDefinition(newDefinition)) as any)
      .then(() => {
        alert.success("Function Saved");
        history.push(`/functions/${newDefinition.id}`);
      })
      .catch((err: any) => {
        if (err) {
          if (isGitHubSetupError(err.message)) {
            setGitHubSetupError(err.message);
          } else {
            alert.error(err.message);
            console.log(err);
          }
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <>
      <GitHubSetupModal
        gitHubError={gitHubSetupError}
        onClose={() => setGitHubSetupError(null)}
      />
      <Grid
        container
        spacing={0}
        alignItems="flex-start"
        alignContent="flex-start"
        className={clsx(classes.widgets, "px-5 pb-2")}
      >
        <Grid item xs={11}>
          <div className={classes.widget}>
            <Card>
              <CardHeader
                title="New Function"
                classes={{ action: clsx(classes.closeIconAction) }}
                action={
                  <Button
                    variant="outlined"
                    onClick={() => history.replace("/functions")}
                  >
                    Cancel
                  </Button>
                }
              />
              <Divider />
              <CardContent>
                <FunctionTypePicker
                  functionType={functionType}
                  onPickFunctionType={setFunctionType}
                />
                <FunctionTextInput
                  label="Function Name *"
                  key="name"
                  name="name"
                  value={functionName}
                  onChangeText={setFunctionName}
                />
                <FunctionTextInput
                  label="GitHub Repo *"
                  key="gitHubRepo"
                  name="gitHubRepo"
                  value={gitHubRepo}
                  onChangeText={setGitHubRepo}
                  placeholder="For example https://github.com/freshpaint-io/functions-template or https://github.com/<org>/<repo>/tree/<branch-name>"
                />
                <FunctionTextInput
                  label="Description (optional)"
                  key="description"
                  name="description"
                  value={description}
                  onChangeText={setDescription}
                  multiline
                />
              </CardContent>
              <Divider />
              <CardActions className={classes.actions}>
                {isSaving && <RefreshIcon className={clsx("animate-spin")} />}
                <Button
                  className={clsx(classes.createFunctionButton, {
                    "animate-pulse": isSaving,
                  })}
                  onClick={onSave}
                  variant="outlined"
                  disabled={isSaving}
                >
                  {!isSaving ? "CREATE" : "CREATING FUNCTION..."}
                </Button>
              </CardActions>
            </Card>
          </div>
        </Grid>
      </Grid>
    </>
  );
};

const FunctionTypePicker: React.FC<{
  functionType: FunctionType | null;
  onPickFunctionType: (type: FunctionType) => void;
}> = ({ functionType, onPickFunctionType }) => {
  const classes = useFunctionStyles();

  return (
    <FormControl
      component="fieldset"
      className={clsx("my-5", "w-full", classes.formControl)}
    >
      <FormLabel id="demo-customized-radios">Function Type</FormLabel>
      <RadioGroup
        value={functionType}
        onChange={(event) =>
          onPickFunctionType(event.target.value as FunctionType)
        }
      >
        <FormControlLabel
          key={0}
          value="SOURCE"
          control={<Radio />}
          label="Source - Ingest data from anywhere into Freshpaint"
        />
        <FormControlLabel
          key={1}
          value="DESTINATION"
          control={<Radio />}
          label="Destination - Send data from Freshpaint to anywhere"
        />
      </RadioGroup>
    </FormControl>
  );
};
